// Product State Engine -- the reusable mapping from REAL product state to a
// GLIM expression. Nothing here is decorative or random: a GLIM state is
// only ever shown because the product is actually in that state.
//
// Rules (Visual System brief):
//   * 15 approved states only; no new character styles.
//   * No random cycling. State comes from `resolveProductState(signals)`.
//   * A critical state (error / warning / approval) is NEVER understood
//     through the character alone -- `GLIMState` always pairs these with a
//     text label (`requiresText: true`).
//   * "Acting" / "Success" describe something the product actually did;
//     callers must only pass `executing` / `completed` for supported,
//     user-approved work (Product Truth). Martian recommends, a human
//     approves where required.

export const GLIM_STATE_IDS = Object.freeze([
  'neutral',
  'curious',
  'thinking',
  'discovering',
  'analyzing',
  'recommending',
  'waiting',
  'approval_required',
  'acting',
  'success',
  'learning',
  'warning',
  'something_changed',
  'error',
  'celebrating',
]);

// motion: which calm motion class (brand.css) the state may use.
//   rest    -- slow idle only
//   think   -- gentle continuous (loading / in-flight work)
//   notice  -- single one-shot nudge (new information)
//   alert   -- single one-shot attention cue (warning / error / approval)
//   celebrate -- single one-shot lift (success / milestone)
export const GLIM_STATE_META = Object.freeze({
  neutral: { label: 'Ready', description: 'Martian is ready.', motion: 'rest', requiresText: false },
  curious: { label: 'Looking into it', description: 'Martian is looking into this.', motion: 'notice', requiresText: false },
  thinking: { label: 'Thinking', description: 'Martian is working on it.', motion: 'think', requiresText: false },
  discovering: { label: 'Discovering', description: 'Martian is scanning for new signals.', motion: 'think', requiresText: false },
  analyzing: { label: 'Analyzing', description: 'Martian is analyzing the evidence.', motion: 'think', requiresText: false },
  recommending: { label: 'Recommending', description: 'Martian has a recommendation.', motion: 'notice', requiresText: false },
  waiting: { label: 'Waiting', description: 'Martian is waiting.', motion: 'rest', requiresText: false },
  approval_required: { label: 'Approval required', description: 'Martian recommends. Human approves where required.', motion: 'alert', requiresText: true },
  acting: { label: 'Acting', description: 'Approved work is in progress.', motion: 'think', requiresText: true },
  success: { label: 'Done', description: 'Completed.', motion: 'celebrate', requiresText: false },
  learning: { label: 'Learning', description: 'Martian is incorporating what happened.', motion: 'think', requiresText: false },
  warning: { label: 'Needs attention', description: 'Something needs attention.', motion: 'alert', requiresText: true },
  something_changed: { label: 'Something changed', description: 'A material change was detected.', motion: 'notice', requiresText: true },
  error: { label: 'Something went wrong', description: 'Something went wrong.', motion: 'alert', requiresText: true },
  celebrating: { label: 'Milestone', description: 'A milestone was reached.', motion: 'celebrate', requiresText: false },
});

// Real product event -> GLIM state. This is the single source of truth for
// "what does the face show when the product does X".
export const PRODUCT_EVENT_TO_STATE = Object.freeze({
  idle: 'neutral',
  empty: 'neutral',
  loading: 'thinking',
  discovering: 'discovering',
  scanning: 'discovering',
  insight: 'analyzing',
  analyzing: 'analyzing',
  recommendation: 'recommending',
  waiting: 'waiting',
  approval: 'approval_required',
  executing: 'acting',
  completed: 'success',
  outcome: 'learning',
  learning: 'learning',
  warning: 'warning',
  problem: 'warning',
  error: 'error',
  changed: 'something_changed',
  milestone: 'celebrating',
});

export function isGlimState(id) {
  return GLIM_STATE_IDS.includes(id);
}

export function stateForEvent(event) {
  return PRODUCT_EVENT_TO_STATE[event] || 'neutral';
}

// Boolean-signal resolver for views that know several things at once.
// Highest-severity wins; the order is fixed so the face never flickers
// between states for the same underlying condition.
const PRECEDENCE = [
  ['error', 'error'],
  ['warning', 'warning'],
  ['executing', 'acting'], // the human already approved; work is in flight
  ['approvalRequired', 'approval_required'],
  ['changed', 'something_changed'],
  ['loading', 'thinking'],
  ['discovering', 'discovering'],
  ['analyzing', 'analyzing'],
  ['recommending', 'recommending'],
  ['learning', 'learning'],
  ['waiting', 'waiting'],
  ['completed', 'success'],
  ['milestone', 'celebrating'],
];

export function resolveProductState(signals = {}) {
  for (const [key, state] of PRECEDENCE) {
    if (signals[key]) return state;
  }
  return 'neutral';
}

export function glimStateMeta(id) {
  return GLIM_STATE_META[id] || GLIM_STATE_META.neutral;
}
